import {
    Button,
    RadioControlGroup,
    RadioInput,
    replaceStr,
    scrollTo,
    Section,
    SectionHeader,
    TextField,
    Tooltip
} from 'flos-react-cvi';
import React from 'react';
import { connect } from 'react-redux';
import { CheckboxField } from '../components/checkbox-field';
import { useScrollOnMount } from '../hooks/use-scroll-on-mount';
import {
    setAccountHolderCprNumber,
    setAccountNum,
    setAgreeObtainInfo,
    setAgreeTermsOfPurchase,
    setIsSomeoneElseAccount,
    setRegNum
} from '../store/actions';
import {
    selectAccountNumIsValid,
    selectAccountOwnerCprIsValid,
    selectCurrentStep,
    selectCustomerEmail,
    selectPaymentInfoIsValid,
    selectPaymentInfoValues,
    selectRegNumIsValid,
    selectResources,
    selectUiStatus
} from '../store/selectors';
import { completePaymentInfo } from '../store/thunks';

const PaymentInfoView = ({
    values: { regNum, accountNum, isSomeoneElseAccount, accountHolderCprNumber, agreeTermsOfPurchase, agreeObtainInfo },
    email,
    resources,
    isFocusOnThisSection,
    regNumIsValid,
    accountNumIsValid,
    ownerCprIsValid,
    paymentInfoIsValid,
    completeSection,
    isSubmitting,
    setRegNumProp,
    setAccountNumProp,
    setAccountHolderCprNumberProp,
    setAgreeObtainInfoProp,
    setAgreeTermsOfPurchaseProp,
    setIsSomeoneElseAccountProp
}) => {
    const scrollTarget = useScrollOnMount();
    const [hasSubmitted, setHasSubmitted] = React.useState(false);
    const formRef = React.useRef();

    React.useEffect(() => {
        if (hasSubmitted && !paymentInfoIsValid && formRef.current) {
            const inputWithError = formRef.current.querySelector('.form-group.has-error input');
            if (inputWithError) {
                scrollTo(inputWithError);
            }
        }
    }, [hasSubmitted, paymentInfoIsValid]);

    function submit() {
        setHasSubmitted(true);
        completeSection();
    }

    return (
        <Section variant={isFocusOnThisSection ? 'alt' : undefined} ref={scrollTarget}>
            <div className="container" data-testid="payment-info" ref={formRef}>
                <SectionHeader>{resources.paymentInfoAndTncSectionHeading}</SectionHeader>
                <div>
                    <h3 className="h4">{resources.paymentAccountTitle}</h3>
                    <p>{resources.paymentAccountSubtitle}</p>
                    <div className="row">
                        <div className="col-xs-6 col-sm-3 col-lg-2">
                            <TextField
                                value={regNum}
                                onChangeValue={setRegNumProp}
                                status={hasSubmitted ? (regNumIsValid ? 'success' : 'error') : null}
                                label={resources.paymentAccountRegNumber}
                                required
                            />
                        </div>
                        <div className="col-xs-6 col-sm-3 col-lg-3">
                            <TextField
                                value={accountNum}
                                onChangeValue={setAccountNumProp}
                                status={hasSubmitted ? (accountNumIsValid ? 'success' : 'error') : null}
                                label={resources.paymentAccountAccountNumber}
                                required
                            />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-xs-12 col-sm-6 col-lg-5 u-Space-up">
                            <RadioControlGroup
                                value={isSomeoneElseAccount}
                                onChangeValue={setIsSomeoneElseAccountProp}
                                label={
                                    <>
                                        {resources.paymentAccountDifferentNameLabel}
                                        <Tooltip label={resources.paymentAccountDifferentNamePopover}>
                                            <flos-icon
                                                style={{ margin: '0 5px' }}
                                                shape="circle-exclamation"
                                                size="20"
                                            />
                                        </Tooltip>
                                    </>
                                }
                                vertical={false}
                            >
                                <RadioInput
                                    value={true}
                                    label={resources.paymentAccountDifferentNameOptionYes}
                                    data-testid="paymentAccountDifferentNameOptionYes"
                                />
                                <RadioInput
                                    value={false}
                                    label={resources.paymentAccountDifferentNameOptionNo}
                                    data-testid="paymentAccountDifferentNameOptionNo"
                                />
                            </RadioControlGroup>
                        </div>
                    </div>
                    {isSomeoneElseAccount && (
                        <div className="row">
                            <div className="col-xs-12 col-sm-6 col-lg-5">
                                <TextField
                                    value={accountHolderCprNumber}
                                    onChangeValue={setAccountHolderCprNumberProp}
                                    status={hasSubmitted ? (ownerCprIsValid ? 'success' : 'error') : null}
                                    errorHelpText={resources.customerInfoSectionCprFormatError}
                                    label={resources.paymentAccountHolderCprLabel}
                                    required
                                />
                            </div>
                        </div>
                    )}
                    <div className="u-Space-up--lg">
                        <h3 className="h4">{resources.tncTitle}</h3>
                        <div className="row u-Space-down--lg">
                            <div className="col-md-7 col-xs-12">
                                <div className="Box">
                                    <CheckboxField
                                        checked={agreeTermsOfPurchase}
                                        data-testid="agree-to-terms-of-purchase"
                                        onChangeValue={setAgreeTermsOfPurchaseProp}
                                        status={hasSubmitted && !agreeTermsOfPurchase && 'error'}
                                        label={
                                            <p
                                                dangerouslySetInnerHTML={{ __html: resources.tncAgreeTermsOfPurchase }}
                                            />
                                        }
                                        disabled={isSubmitting}
                                    />
                                </div>
                                <div className="Box">
                                    <CheckboxField
                                        checked={agreeObtainInfo}
                                        onChangeValue={setAgreeObtainInfoProp}
                                        status={hasSubmitted && !agreeObtainInfo && 'error'}
                                        data-testid="agree-obtain-info"
                                        label={<p dangerouslySetInnerHTML={{ __html: resources.tncAgreeObtainInfo }} />}
                                        disabled={isSubmitting}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <p
                    className="u-Space--up"
                    dangerouslySetInnerHTML={{
                        __html: replaceStr(resources.paymentInfoAndTncSectionSendReceiptNote, {
                            '{email}': email
                        })
                    }}
                />

                <p className="u-Space-down--lg">{resources.legaltext}</p>
                {isFocusOnThisSection && (
                    <div className="row">
                        <div className="col-xs-12">
                            <Button
                                onClick={submit}
                                isLoading={isSubmitting}
                                disabled={isSubmitting}
                                variant="primary"
                                minWidth="wide"
                            >
                                {resources.paymentInfoAndTncSectionButtonText}
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </Section>
    );
};

const mapStates = state => ({
    values: selectPaymentInfoValues(state),
    email: selectCustomerEmail(state),
    regNumIsValid: selectRegNumIsValid(state),
    accountNumIsValid: selectAccountNumIsValid(state),
    ownerCprIsValid: selectAccountOwnerCprIsValid(state),
    paymentInfoIsValid: selectPaymentInfoIsValid(state),
    resources: selectResources(state),
    isFocusOnThisSection: selectCurrentStep(state) === 4,
    isSubmitting: selectUiStatus(state) === 'submitting'
});

const mapDispatch = dispatch => ({
    setRegNumProp: num => dispatch(setRegNum(num)),
    setAccountNumProp: num => dispatch(setAccountNum(num)),
    setAccountHolderCprNumberProp: cpr => dispatch(setAccountHolderCprNumber(cpr)),
    setAgreeObtainInfoProp: checked => dispatch(setAgreeObtainInfo(checked)),
    setAgreeTermsOfPurchaseProp: checked => dispatch(setAgreeTermsOfPurchase(checked)),
    setIsSomeoneElseAccountProp: checked => dispatch(setIsSomeoneElseAccount(checked)),
    completeSection: () => dispatch(completePaymentInfo())
});

export const PaymentInfo = connect(mapStates, mapDispatch)(PaymentInfoView);
